import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabaseClient.js";
import "../../styles/ReceiveWax.css";

const ReceiveWax = () => {
  const [lots, setLots] = useState([]);
  const [popupItem, setPopupItem] = useState(null);

  // For QR auto highlight
  const scrollRef = useRef({});
  const highlightLotNo = new URLSearchParams(window.location.search).get("lot");

  useEffect(() => {
    fetchLots();
  }, []);

  useEffect(() => {
    if (highlightLotNo && lots.length > 0) {
      const target = lots.find((l) => l.lot_no === highlightLotNo);
      if (target && scrollRef.current[target.id]) {
        scrollRef.current[target.id].scrollIntoView({ behavior: "smooth", block: "center" });
        scrollRef.current[target.id].classList.add("highlight-lot");

        setTimeout(() => {
          scrollRef.current[target.id]?.classList.remove("highlight-lot");
        }, 4000);
      }
    }
  }, [lots, highlightLotNo]);

  const fetchLots = async () => {
    const { data: lotsData } = await supabase
      .from("WAX_LOTS")
      .select("id, lot_no, worker_id, issue_date, status")
      .eq("status", "ISSUED")
      .order("issue_date", { ascending: false });

    if (!lotsData) return;

    const lotIds = lotsData.map((l) => l.id);

    const { data: items } = await supabase
      .from("WAX_ITEMS")
      .select(`
        id,
        lot_id,
        item_id,
        design_code,
        gross_weight,
        boxes_count,
        per_box_weight,
        total_box_weight,
        bags_count,
        per_bag_weight,
        total_bag_weight,
        net_weight,
        pieces_count,
        item_name:ITEMS(item_name)
      `)
      .in("lot_id", lotIds);

    const { data: received } = await supabase
      .from("WAX_RECEIVE_ITEMS")
      .select("item_id");

    const receivedSet = new Set(received?.map((r) => r.item_id));

    const merged = lotsData.map((lot) => ({
      ...lot,
      items: items
        .filter((i) => i.lot_id === lot.id)
        .map((i) => ({ ...i, is_received: receivedSet.has(i.id) })),
    }));

    setLots(merged);
  };

  const markLotComplete = async (lotId) => {
    await supabase.from("WAX_LOTS").update({ status: "COMPLETED" }).eq("id", lotId);
    fetchLots();
    alert("Lot marked as completed!");
  };

  const printLot = async (lot) => {
    const { data: items } = await supabase
      .from("WAX_ITEMS")
      .select(`
        id,
        design_code,
        gross_weight,
        net_weight,
        pieces_count,
        boxes_count,
        per_box_weight,
        total_box_weight,
        bags_count,
        per_bag_weight,
        total_bag_weight,
        item_name:ITEMS(item_name)
      `)
      .eq("lot_id", lot.id);

    const win = window.open("", "_blank", "width=900,height=700");

    const html = `
      <html>
        <head>
          <title>Print Lot ${lot.lot_no}</title>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
          <style>
            body { font-family: Arial; padding: 25px; }
            .header { display: flex; justify-content: space-between; align-items: center; }
            .qr-box canvas { width: 160px; height: 160px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #000; padding: 10px; vertical-align: top; }
            th { background: #f3f3f3; }
          </style>
        </head>
        <body>

          <div class="header">
            <div>
              <h2><b>Lot No:</b> ${lot.lot_no}</h2>
              <h3><b>Date:</b> ${lot.issue_date.slice(0, 10)}</h3>
            </div>
            <div class="qr-box"><canvas id="qrCanvas"></canvas></div>
          </div>

          <table>
            <tr><th style="width:50px;">#</th><th>Details</th></tr>
            ${items
              .map(
                (i, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>
                    <b>${i.item_name.item_name}</b> (${i.design_code})<br/>
                    Gross: ${i.gross_weight}g | Net: ${i.net_weight}g | Pcs: ${i.pieces_count}<br/>
                    Boxes: ${i.boxes_count} × ${i.per_box_weight} = ${i.total_box_weight}<br/>
                    Bags: ${i.bags_count} × ${i.per_bag_weight} = ${i.total_bag_weight}
                  </td>
                </tr>`
              )
              .join("")}
          </table>

          <script>
            new QRious({
              element: document.getElementById('qrCanvas'),
              value: '${lot.lot_no}',
              size: 200,
              level: 'H'
            });

            window.onload = () => { window.print(); window.close(); };
          </script>
        </body>
      </html>
    `;

    win.document.write(html);
    win.document.close();
  };

  // =======================================
  // SAVE RECEIVE
  // =======================================
  const saveReceive = async (fields) => {
    const {
      lot_id,
      item_id,
      issued_net,
      issued_pieces,
      rejected_pieces,
      micro_stone,
      big_stone,
      useSame,
      received_gross,
      boxes_count,
      per_box_weight,
      bags_count,
      per_bag_weight,
    } = fields;

    // SAFE MATH
    const safeIssuedPcs = Number(issued_pieces) > 0 ? Number(issued_pieces) : 0;
    const avg = safeIssuedPcs === 0 ? 0 : Number(issued_net) / safeIssuedPcs;
    const rejectedWeight = safeIssuedPcs === 0 ? 0 : +(rejected_pieces * avg).toFixed(3);

    const totalBoxWeight = boxes_count * per_box_weight;
    const totalBagWeight = bags_count * per_bag_weight;

    const receivedNet = +(
      Number(received_gross || 0) -
      totalBoxWeight -
      totalBagWeight
    ).toFixed(3);

    const expectedNet =
      safeIssuedPcs === 0
        ? Number(issued_net)
        : +(Number(issued_net) - rejectedWeight).toFixed(3);

    const gainLoss = +(receivedNet - expectedNet).toFixed(3);

    await supabase.from("WAX_RECEIVE_ITEMS").insert([
      {
        lot_id,
        item_id,
        received_boxes_count: boxes_count,
        received_per_box_weight: per_box_weight,
        received_total_box_weight: totalBoxWeight,
        received_bags_count: bags_count,
        received_per_bag_weight: per_bag_weight,
        received_total_bag_weight: totalBagWeight,
        rejected_pieces,
        received_pieces_count: safeIssuedPcs - rejected_pieces,
        received_net_weight: receivedNet,
        micro_stone,
        big_stone,
        gain_loss: gainLoss,
      },
    ]);

    await supabase
      .from("WAX_ITEMS")
      .update({
        received_gross: received_gross,
        received_net: receivedNet,
        received_pcs: safeIssuedPcs - rejected_pieces,
      })
      .eq("id", item_id);

    setPopupItem(null);
    fetchLots();
    alert("Item received!");
  };

  // =======================================
  // POPUP — FIXED VERSION
  // =======================================
  const ReceivePopup = ({ item, lot, onClose }) => {
    const [itemName, setItemName] = useState("");

    useEffect(() => {
      loadName();
    }, []);

    const loadName = async () => {
      const { data } = await supabase
        .from("ITEMS")
        .select("item_name")
        .eq("id", item.item_id)
        .single();

      if (data) setItemName(data.item_name);
    };

    const [useSame, setUseSame] = useState(true);
    const [receivedGross, setReceivedGross] = useState("");
    const [boxes, setBoxes] = useState(item.boxes_count);
    const [perBox, setPerBox] = useState(item.per_box_weight);
    const [bags, setBags] = useState(item.bags_count);
    const [perBag, setPerBag] = useState(item.per_bag_weight);
    const [rejected, setRejected] = useState(0);
    const [micro, setMicro] = useState(0);
    const [big, setBig] = useState(0);

    // SAFE MATH HERE
    const safePcs = Number(item.pieces_count) > 0 ? Number(item.pieces_count) : 0;

    const avg = safePcs === 0 ? 0 : Number(item.net_weight) / safePcs;
    const rejectedWeight = safePcs === 0 ? 0 : +(rejected * avg).toFixed(3);

    const totalBoxWeight = (boxes || 0) * (perBox || 0);
    const totalBagWeight = (bags || 0) * (perBag || 0);

    const receivedNet =
      receivedGross === ""
        ? 0
        : +(
            Number(receivedGross || 0) -
            totalBoxWeight -
            totalBagWeight
          ).toFixed(3);

    const expectedNet =
      safePcs === 0
        ? Number(item.net_weight)
        : +(Number(item.net_weight) - rejectedWeight).toFixed(3);

    const gainLoss = +(
      Number(receivedNet || 0) - Number(expectedNet || 0)
    ).toFixed(3);

    return (
      <div className="popup-overlay">
        <div className="popup-box">

          <h2 className="popup-title">Receive Item – {lot.lot_no}</h2>

          <div className="popup-line"><b>Item:</b> {itemName}</div>
          <div className="popup-line"><b>Design:</b> {item.design_code}</div>

          <h3>Issued Details</h3>
          <div className="popup-grid">
            <div>Gross: {item.gross_weight}</div>
            <div>Net: {item.net_weight}</div>
            <div>Pcs: {item.pieces_count}</div>
            <div>Avg: {avg.toFixed(3)}</div>
          </div>

          <h3>Receiving Details</h3>
          <label className="checkbox-line">
            <input
              type="checkbox"
              checked={useSame}
              onChange={() => {
                setUseSame(!useSame);
                if (!useSame) {
                  setBoxes(item.boxes_count);
                  setPerBox(item.per_box_weight);
                  setBags(item.bags_count);
                  setPerBag(item.per_bag_weight);
                }
              }}
            />
            Use same issued boxes/bags
          </label>

          <div className="popup-grid">
            <div>
              <label>Received Gross</label>
              <input
                type="number"
                value={receivedGross}
                onChange={(e) => setReceivedGross(+e.target.value)}
              />
            </div>

            <div>
              <label>Boxes</label>
              <input
                type="number"
                disabled={useSame}
                value={boxes}
                onChange={(e) => setBoxes(+e.target.value)}
              />
            </div>

            <div>
              <label>Wt/Box</label>
              <input
                type="number"
                disabled={useSame}
                value={perBox}
                onChange={(e) => setPerBox(+e.target.value)}
              />
            </div>

            <div>
              <label>Bags</label>
              <input
                type="number"
                disabled={useSame}
                value={bags}
                onChange={(e) => setBags(+e.target.value)}
              />
            </div>

            <div>
              <label>Wt/Bag</label>
              <input
                type="number"
                disabled={useSame}
                value={perBag}
                onChange={(e) => setPerBag(+e.target.value)}
              />
            </div>
          </div>

          <h3>Rejected & Stones</h3>
          <div className="popup-grid">
            <div>
              <label>Rejected Pcs</label>
              <input
                type="number"
                value={rejected}
                onChange={(e) => setRejected(+e.target.value)}
              />
            </div>

            <div>
              <label>Micro Stone</label>
              <input
                type="number"
                value={micro}
                onChange={(e) => setMicro(+e.target.value)}
              />
            </div>

            <div>
              <label>Big Stone</label>
              <input
                type="number"
                value={big}
                onChange={(e) => setBig(+e.target.value)}
              />
            </div>
          </div>

          <h3>Calculated</h3>
          <div className="popup-grid">
            <div>Rejected Wt: {rejectedWeight}</div>
            <div>Expected Net: {expectedNet}</div>
            <div>Received Net: {receivedNet}</div>
            <div>Gain/Loss: {gainLoss}</div>
          </div>

          <div className="popup-buttons">
            <button
              className="wax-btn"
              onClick={() =>
                saveReceive({
                  lot_id: lot.id,
                  item_id: item.id,
                  issued_net: item.net_weight,
                  issued_pieces: item.pieces_count,
                  rejected_pieces: rejected,
                  micro_stone: micro,
                  big_stone: big,
                  useSame,
                  received_gross: receivedGross,
                  boxes_count: useSame ? item.boxes_count : boxes,
                  per_box_weight: useSame ? item.per_box_weight : perBox,
                  bags_count: useSame ? item.bags_count : bags,
                  per_bag_weight: useSame ? item.per_bag_weight : perBag,
                })
              }
            >
              Save Receive
            </button>

            <button className="wax-btn" onClick={onClose}>
              Close
            </button>
          </div>

        </div>
      </div>
    );
  };

  return (
    <div className="receive-container">
      <style>
        {`
          .highlight-lot {
            animation: flashHighlight 1s ease-in-out 4;
          }
          @keyframes flashHighlight {
            0% { background-color: #ffeb3b; }
            50% { background-color: #fff; }
            100% { background-color: #ffeb3b; }
          }
        `}
      </style>

      <h2 className="page-title">Receive Wax</h2>

      {lots.length === 0 && <p className="no-lots">No ongoing lots.</p>}

      {lots.map((lot) => (
        <div
          className="lot-card"
          key={lot.id}
          ref={(el) => (scrollRef.current[lot.id] = el)}
        >
          <div className="lot-header">
            <h3>{lot.lot_no}</h3>
            <p>{lot.issue_date}</p>

            <div className="lot-top-buttons">
              <button
                className="wax-btn"
                onClick={() =>
                  (window.location.href = `/wax-issue?continueLot=${lot.lot_no}`)
                }
              >
                + Add Items
              </button>

              <button className="wax-btn" onClick={() => printLot(lot)}>
                Print
              </button>
            </div>
          </div>

          <table className="wax-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Design</th>
                <th>Gross</th>
                <th>Net</th>
                <th>Pcs</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {lot.items.map((i) => (
                <tr key={i.id}>
                  <td>{i.item_name?.item_name}</td>
                  <td>{i.design_code}</td>
                  <td>{i.gross_weight}</td>
                  <td>{i.net_weight}</td>
                  <td>{i.pieces_count}</td>
                  <td>
                    {i.is_received ? (
                      <span className="green">Completed</span>
                    ) : (
                      <span className="red">Pending</span>
                    )}
                  </td>
                  <td>
                    {!i.is_received && (
                      <button
                        className="wax-btn"
                        onClick={() => setPopupItem({ lot, item: i })}
                      >
                        Receive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            className="wax-btn mark-btn"
            onClick={() => markLotComplete(lot.id)}
          >
            Mark Lot Complete
          </button>
        </div>
      ))}

      {popupItem && (
        <ReceivePopup
          lot={popupItem.lot}
          item={popupItem.item}
          onClose={() => setPopupItem(null)}
        />
      )}
    </div>
  );
};

export default ReceiveWax;
